import { useCallback, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import type { MapNode } from '../api/types';

interface FlatNode {
  path: string;
  depth: number;
  node: MapNode;
  parent: string | null;
  hasChildren: boolean;
}

function nodeLabel(node: MapNode): string {
  return node.label ?? 'Unlabeled branch';
}

function flatten(nodes: MapNode[], expanded: Set<string>, parent: string | null = null, depth = 1): FlatNode[] {
  const rows: FlatNode[] = [];
  nodes.forEach((node, index) => {
    const path = parent === null ? String(index) : `${parent}.${String(index)}`;
    const children = node.children ?? [];
    rows.push({ path, depth, node, parent, hasChildren: children.length > 0 });
    if (children.length > 0 && expanded.has(path)) rows.push(...flatten(children, expanded, path, depth + 1));
  });
  return rows;
}

function allBranchPaths(nodes: MapNode[], parent: string | null = null): string[] {
  const paths: string[] = [];
  nodes.forEach((node, index) => {
    const path = parent === null ? String(index) : `${parent}.${String(index)}`;
    const children = node.children ?? [];
    if (children.length > 0) paths.push(path, ...allBranchPaths(children, path));
  });
  return paths;
}

function matchingPaths(nodes: MapNode[], query: string, parent: string | null = null): { hits: Set<string>; open: Set<string> } {
  const hits = new Set<string>();
  const open = new Set<string>();
  nodes.forEach((node, index) => {
    const path = parent === null ? String(index) : `${parent}.${String(index)}`;
    const nested = matchingPaths(node.children ?? [], query, path);
    nested.hits.forEach((hit) => hits.add(hit));
    nested.open.forEach((item) => open.add(item));
    if (nested.hits.size > 0) open.add(path);
    if ((node.label ?? '').toLowerCase().includes(query)) hits.add(path);
  });
  return { hits, open };
}

export function countNodes(nodes: MapNode[]): number {
  return nodes.reduce((total, node) => total + 1 + countNodes(node.children ?? []), 0);
}

/**
 * An expandable, keyboard-operable transcription of an original map, following
 * the WAI-ARIA tree pattern. Unlabeled and collapsed branches in the original
 * are shown as such; nothing hidden in the image is reconstructed.
 */
export function MapTree({ nodes, label, initialDepth = 2 }: { nodes: MapNode[]; label: string; initialDepth?: number }) {
  const initial = useMemo(() => {
    const open = new Set<string>();
    const walk = (list: MapNode[], parent: string | null, depth: number) => {
      list.forEach((node, index) => {
        const path = parent === null ? String(index) : `${parent}.${String(index)}`;
        if (depth < initialDepth && (node.children ?? []).length > 0) {
          open.add(path);
          walk(node.children ?? [], path, depth + 1);
        }
      });
    };
    walk(nodes, null, 0);
    return open;
  }, [nodes, initialDepth]);

  const [expanded, setExpanded] = useState<Set<string>>(initial);
  const [focused, setFocused] = useState<string>('0');
  const [query, setQuery] = useState('');
  const refs = useRef(new Map<string, HTMLLIElement>());

  const search = query.trim().toLowerCase();
  const matches = useMemo(() => (search === '' ? null : matchingPaths(nodes, search)), [nodes, search]);
  const effectiveExpanded = useMemo(
    () => (matches === null ? expanded : new Set([...expanded, ...matches.open])),
    [expanded, matches],
  );
  const rows = useMemo(() => flatten(nodes, effectiveExpanded), [nodes, effectiveExpanded]);
  const focusPath = rows.some((row) => row.path === focused) ? focused : (rows[0]?.path ?? '0');

  const moveTo = useCallback((path: string) => {
    setFocused(path);
    refs.current.get(path)?.focus();
  }, []);

  const toggle = useCallback((path: string, open?: boolean) => {
    setExpanded((current) => {
      const next = new Set(current);
      const shouldOpen = open ?? !next.has(path);
      if (shouldOpen) next.add(path);
      else next.delete(path);
      return next;
    });
  }, []);

  function onKeyDown(event: KeyboardEvent<HTMLLIElement>, row: FlatNode) {
    const index = rows.findIndex((candidate) => candidate.path === row.path);
    const isOpen = effectiveExpanded.has(row.path);
    switch (event.key) {
      case 'ArrowDown': {
        const next = rows[index + 1];
        if (next !== undefined) moveTo(next.path);
        break;
      }
      case 'ArrowUp': {
        const previous = rows[index - 1];
        if (previous !== undefined) moveTo(previous.path);
        break;
      }
      case 'ArrowRight':
        if (row.hasChildren && !isOpen) toggle(row.path, true);
        else if (row.hasChildren) {
          const child = rows[index + 1];
          if (child !== undefined) moveTo(child.path);
        }
        break;
      case 'ArrowLeft':
        if (row.hasChildren && isOpen) toggle(row.path, false);
        else if (row.parent !== null) moveTo(row.parent);
        break;
      case 'Home': {
        const first = rows[0];
        if (first !== undefined) moveTo(first.path);
        break;
      }
      case 'End': {
        const last = rows[rows.length - 1];
        if (last !== undefined) moveTo(last.path);
        break;
      }
      case 'Enter':
      case ' ':
        if (row.hasChildren) toggle(row.path);
        break;
      default:
        return;
    }
    event.preventDefault();
    event.stopPropagation();
  }

  const total = countNodes(nodes);

  return (
    <div className="map-tree">
      <div className="map-tree__tools">
        <label className="field field--inline">
          <span className="field__label">Find in map</span>
          <input
            type="search"
            className="input"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="e.g. mentorship"
          />
        </label>
        <div className="button-row">
          <button type="button" className="button button--secondary" onClick={() => setExpanded(new Set(allBranchPaths(nodes)))}>
            Expand all
          </button>
          <button type="button" className="button button--secondary" onClick={() => setExpanded(new Set())}>
            Collapse all
          </button>
        </div>
      </div>
      <p className="map-tree__status" role="status" aria-live="polite">
        {matches === null
          ? `${String(total)} visible labels transcribed. Use arrow keys to move and expand.`
          : `${String(matches.hits.size)} of ${String(total)} labels match “${query.trim()}”.`}
      </p>
      <ul role="tree" aria-label={label} className="map-tree__list">
        {rows.map((row) => {
          const isOpen = effectiveExpanded.has(row.path);
          const collapsedInOriginal = row.node.children_visibility === 'collapsed';
          const unlabeled = row.node.label === null;
          const hit = matches?.hits.has(row.path) ?? false;
          return (
            <li
              key={row.path}
              ref={(element) => {
                if (element === null) refs.current.delete(row.path);
                else refs.current.set(row.path, element);
              }}
              role="treeitem"
              aria-level={row.depth}
              aria-expanded={row.hasChildren ? isOpen : undefined}
              aria-selected={row.path === focusPath}
              tabIndex={row.path === focusPath ? 0 : -1}
              onKeyDown={(event) => onKeyDown(event, row)}
              onFocus={() => setFocused(row.path)}
              className={`map-node${hit ? ' map-node--hit' : ''}${unlabeled ? ' map-node--unlabeled' : ''}`}
              style={{ ['--depth' as string]: row.depth - 1 }}
            >
              <span className="map-node__row">
                {row.hasChildren ? (
                  <button
                    type="button"
                    tabIndex={-1}
                    aria-hidden="true"
                    className="map-node__toggle"
                    onClick={() => {
                      toggle(row.path);
                      moveTo(row.path);
                    }}
                  >
                    {isOpen ? '−' : '+'}
                  </button>
                ) : (
                  <span className="map-node__leaf" aria-hidden="true" />
                )}
                <span className="map-node__label">{nodeLabel(row.node)}</span>
                {unlabeled && <span className="tag tag--warn">No label inferred</span>}
                {collapsedInOriginal && (
                  <span className="tag tag--warn">
                    Collapsed in original{row.node.displayed_badge !== undefined ? ` (badge ${row.node.displayed_badge})` : ''} —
                    not reconstructed
                  </span>
                )}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
